/* eslint-disable */
import React, {createContext, useEffect, useMemo, useState, useCallback, useContext, useRef} from 'react'
import {THEME_ORDER, THEME_META} from 'src/config/entraIds'
import {THEMES} from "../config/themes";
import {detectThemeFromHost} from "src/config/tenants";
import {THEME_KEY} from "src/config/storageKeys";

export const ThemeContext = createContext({
  theme: THEMES.LDA_LIGHT,
  setTheme: () => {
  },
  toggleTheme: () => {
  },
  availableThemes: THEME_ORDER,
  syncThemesForUser: () => {
  },
  brandLabel: 'LDH Explorer',
})

export const THEME_STORAGE_KEY = THEME_KEY

const VALID_THEMES = new Set(THEME_ORDER)
const AVAILABLE_THEMES = THEMES
const VENDOR_THEMES = THEME_ORDER.filter(
  (t) => t !== THEMES.LDA_LIGHT && t !== THEMES.LDA_DARK,
)

export const normalizeTheme = (value) => {
  if (VALID_THEMES.has(value)) return value
  return THEMES.LDA_LIGHT
}

const getSystemTheme = () => {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.LDA_DARK : THEMES.LDA_LIGHT
  } catch {
    return THEMES.LDA_LIGHT
  }
}

const readStoredTheme = () => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY)
    return VALID_THEMES.has(saved) ? saved : null
  } catch {
    return null
  }
}

const resolveDefaultTheme = (filtered, preferredTheme) => {
  const hostTheme = detectThemeFromHost(window.location.hostname)
  if (hostTheme && filtered.includes(hostTheme)) return hostTheme

  if (preferredTheme && filtered.includes(preferredTheme)) return preferredTheme

  const availableVendorThemes = filtered.filter((t) => VENDOR_THEMES.includes(t))
  if (availableVendorThemes.length === 1) return availableVendorThemes[0]

  return THEMES.LDA_LIGHT
}

export const ThemeProvider = ({children, defaultTheme}) => {
  const [availableThemes, setAvailableThemes] = useState(THEME_ORDER)
  const lastSyncedRolesRef = useRef(null)


  const [theme, setThemeState] = useState(() => {
    const detected = detectThemeFromHost(window.location.hostname)
    if (detected) {
      return normalizeTheme(detected)
    }

    const stored = readStoredTheme()
    if (stored) return stored

    return normalizeTheme(defaultTheme || getSystemTheme())
  })

  const setTheme = useCallback((next) => {
    setThemeState((prev) => {
      const candidate = normalizeTheme(next)
      return availableThemes.includes(candidate) ? candidate : prev
    })
  }, [availableThemes])

  const syncThemesForUser = useCallback((allowed, preferredTheme) => {
    if (!Array.isArray(allowed) || allowed.length === 0) return
    const filtered = allowed.filter((t) => VALID_THEMES.has(t))
    if (filtered.length === 0) return

    setAvailableThemes((prev) => {
      if (prev.length === filtered.length && prev.every((t, i) => t === filtered[i])) {
        return prev
      }
      return filtered
    })

    const sameRolesAsLastSync =
      lastSyncedRolesRef.current !== null &&
      lastSyncedRolesRef.current.length === filtered.length &&
      lastSyncedRolesRef.current.every((t, i) => t === filtered[i])
    lastSyncedRolesRef.current = filtered

    setThemeState((current) => {
      if (sameRolesAsLastSync && filtered.includes(current)) {
        return current
      }
      return resolveDefaultTheme(filtered, preferredTheme)
    })
  }, [])

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const idx = availableThemes.indexOf(prev)
      const next = availableThemes[(idx + 1) % availableThemes.length]
      return next || prev
    })
  }, [availableThemes])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      // ignore
    }
  }, [theme])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e) => {
      try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY)
        if (!saved) {
          setThemeState(e.matches ? THEMES.LDA_LIGHT : THEMES.LDA_DARK)
        }
      } catch {
        // ignore
      }
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const brandLabel = useMemo(
    () => THEME_META[theme]?.brandLabel || 'LDH Explorer',
    [theme],
  )

  const value = useMemo(
    () => ({theme, setTheme, toggleTheme, availableThemes, syncThemesForUser, brandLabel}),
    [theme, setTheme, toggleTheme, availableThemes, syncThemesForUser, brandLabel],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
